import { Head, Link } from "@inertiajs/react"
import Navbar from "@/components/chalky_booking/Navbar"
import Footer from "@/components/chalky_booking/Footer"

interface ConfidentialityProps {
  brand: { line1: string; line2: string }
  nav: { services: string; agency: string; contact: string }
  footer: {
    description: string
    navigation: string
    contact: string
    copyright: string
    legal: string
    privacy: string
  }
  contact_email: string
  paths: { mentions_legales: string; confidentiality: string }
  locales: string[]
  current_locale: string
  language_labels: Record<string, string>
  last_updated: string
}

const SectionTitle = ({
  children,
  accent = "primary",
}: {
  children: React.ReactNode
  accent?: "primary" | "secondary"
}) => (
  <h2
    className={`text-2xl md:text-3xl font-bold text-contrast mb-6 pb-3 border-b-2 ${
      accent === "primary" ? "border-primary/20" : "border-secondary/20"
    }`}
  >
    {children}
  </h2>
)

export default function Confidentiality({
  brand,
  nav,
  footer,
  contact_email,
  paths,
  locales,
  current_locale,
  language_labels,
  last_updated,
}: ConfidentialityProps) {
  return (
    <>
      <Head>
        <title>Politique de confidentialité — Chalky Booking</title>
        <meta name="description" content="Politique de confidentialité de Chalky Booking et de l'association CHALKY PRODUCTION." />
      </Head>

      <Navbar
        brand={brand}
        nav={nav}
        locales={locales}
        currentLocale={current_locale}
        languageLabels={language_labels}
      />

      <main className="min-h-screen bg-gradient-to-br from-light via-white to-light pb-12 pt-24">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-contrast mb-4">
              Politique de<span className="text-primary"> Confidentialité</span>
            </h1>
            <p className="text-xl text-content max-w-2xl mx-auto">
              Votre vie privée est importante pour nous. Cette politique explique comment CHALKY PRODUCTION collecte, utilise et protège vos données personnelles.
            </p>
            <div className="text-sm text-content/70 mt-4">
              Dernière mise à jour : {last_updated}
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <div className="mb-12">
              <SectionTitle>1. Introduction</SectionTitle>
              <div className="prose max-w-none text-content leading-relaxed">
                <p className="mb-4">
                  CHALKY PRODUCTION, association loi 1901 située au 26 Bis rue Aristide Briand, 44230 Saint-Sébastien-sur-Loire, s'engage à protéger la confidentialité des données personnelles de ses utilisateurs conformément au Règlement Général sur la Protection des Données (RGPD).
                </p>
                <p>
                  Cette politique de confidentialité décrit comment nous collectons, utilisons, stockons et protégeons vos informations personnelles dans le cadre de nos activités de booking et de production de concerts.
                </p>
              </div>
            </div>

            <div className="mb-12">
              <SectionTitle>2. Données personnelles collectées</SectionTitle>
              <div className="prose max-w-none text-content leading-relaxed">
                <p className="mb-4"><strong>Données de contact :</strong></p>
                <ul className="list-disc pl-6 mb-6 space-y-2">
                  <li>Nom et prénom</li>
                  <li>Adresse e-mail</li>
                  <li>Contenu de vos messages via le formulaire de contact</li>
                </ul>
                <div className="bg-secondary/10 p-6 rounded-xl border-l-4 border-secondary">
                  <p className="font-semibold text-secondary mb-2">Information importante</p>
                  <p className="text-content/90">
                    En tant qu'association à but non lucratif dédiée à la promotion musicale, nous ne collectons que les données strictement nécessaires pour répondre à vos demandes et améliorer nos services.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <SectionTitle>3. Finalités du traitement</SectionTitle>
              <div className="prose max-w-none text-content leading-relaxed">
                <p className="mb-4">Vos données personnelles sont traitées pour les finalités suivantes :</p>
                <ul className="list-disc pl-6 space-y-3">
                  <li><strong>Gestion des demandes de contact :</strong> répondre à vos questions et demandes de booking</li>
                  <li><strong>Mise en relation :</strong> connecter les artistes avec les salles et festivals</li>
                  <li><strong>Communication associative :</strong> vous informer de nos actualités et événements (avec votre consentement)</li>
                  <li><strong>Amélioration de nos services :</strong> analyser l'utilisation de notre site pour l'optimiser</li>
                  <li><strong>Obligations légales :</strong> respecter nos obligations comptables associatives</li>
                </ul>
              </div>
            </div>

            <div className="mb-12">
              <SectionTitle>4. Base légale du traitement</SectionTitle>
              <div className="prose max-w-none text-content leading-relaxed">
                <p className="mb-4">Le traitement de vos données repose sur :</p>
                <ul className="list-disc pl-6 space-y-3">
                  <li><strong>Intérêt légitime :</strong> pour répondre à vos demandes de contact et de booking</li>
                  <li><strong>Consentement :</strong> pour la communication sur nos actualités et événements</li>
                  <li><strong>Obligation légale :</strong> pour le respect des obligations comptables associatives</li>
                </ul>
              </div>
            </div>

            <div className="mb-12">
              <SectionTitle>5. Durée de conservation des données</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-green-500/10 to-green-600/20 p-6 rounded-xl">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-green-600 mb-3">Données de contact</h4>
                  <p className="text-sm text-content/80">Conservées 3 ans après le dernier contact</p>
                </div>
                <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/20 p-6 rounded-xl">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-blue-600 mb-3">Données newsletter</h4>
                  <p className="text-sm text-content/80">Conservées jusqu'au désabonnement</p>
                </div>
              </div>
              <div className="mt-6 bg-orange-500/10 p-6 rounded-xl border-l-4 border-orange-500">
                <p className="font-semibold text-orange-600 mb-2">Gestion responsable</p>
                <p className="text-content/90">
                  Les données sont automatiquement supprimées à l'expiration des délais légaux ou sur demande de votre part.
                </p>
              </div>
            </div>

            <div className="mb-12">
              <SectionTitle accent="secondary">6. Vos droits sur vos données</SectionTitle>
              <div className="bg-gradient-to-br from-purple-500/5 to-purple-600/10 p-8 rounded-xl border-l-4 border-purple-500">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-purple-600 mb-2">Vos droits RGPD</h3>
                  <p className="text-content/90 mb-4">
                    Vous disposez de droits sur vos données personnelles que vous pouvez exercer à tout moment.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold text-purple-600 mb-3">Droit d'accès et de rectification</h4>
                    <ul className="list-disc pl-4 text-sm text-content/80 space-y-2">
                      <li>Consulter vos données personnelles</li>
                      <li>Corriger les informations inexactes</li>
                      <li>Compléter les données incomplètes</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-purple-600 mb-3">Droit d'effacement et d'opposition</h4>
                    <ul className="list-disc pl-4 text-sm text-content/80 space-y-2">
                      <li>Supprimer vos données personnelles</li>
                      <li>Vous opposer au traitement</li>
                      <li>Limiter l'utilisation de vos données</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="mt-6 prose max-w-none text-content leading-relaxed">
                <p className="mb-4">
                  Pour exercer ces droits, contactez-nous à :
                  <a href={`mailto:${contact_email}`} className="text-purple-600 font-semibold hover:underline ml-1">
                    {contact_email}
                  </a>
                </p>
                <p>Nous vous répondrons dans un délai de 30 jours maximum.</p>
              </div>
            </div>

            <div className="mb-12">
              <SectionTitle>7. Sécurité et protection des données</SectionTitle>
              <div className="prose max-w-none text-content leading-relaxed">
                <p className="mb-6">
                  CHALKY PRODUCTION met en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données personnelles.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-br from-green-500/10 to-green-600/20 p-6 rounded-xl text-center">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-green-600 mb-2">Chiffrement</h4>
                    <p className="text-sm text-content/80">Connexions HTTPS et stockage sécurisé</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/20 p-6 rounded-xl text-center">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-blue-600 mb-2">Accès restreint</h4>
                    <p className="text-sm text-content/80">Seuls les membres autorisés accèdent aux données</p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/20 p-6 rounded-xl text-center">
                    <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-orange-600 mb-2">Sauvegarde</h4>
                    <p className="text-sm text-content/80">Sauvegardes régulières et sécurisées</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <SectionTitle accent="secondary">8. Partage et transfert des données</SectionTitle>
              <div className="prose max-w-none text-content leading-relaxed">
                <p className="mb-6">
                  CHALKY PRODUCTION ne vend, ne loue et ne partage vos données personnelles avec des tiers à des fins commerciales.
                </p>
                <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/20 p-6 rounded-xl border-l-4 border-blue-500">
                  <h4 className="font-bold text-blue-600 mb-3">Partenaires techniques nécessaires</h4>
                  <p className="text-content/90 mb-4">
                    Vos données peuvent être partagées uniquement avec nos partenaires techniques indispensables :
                  </p>
                  <ul className="list-disc pl-4 text-content/80 space-y-2">
                    <li><strong>Hébergeur (Clever Cloud) :</strong> pour le stockage sécurisé des données</li>
                  </ul>
                </div>
                <div className="mt-6 bg-green-500/10 p-6 rounded-xl border-l-4 border-green-500">
                  <p className="font-semibold text-green-600 mb-2">Engagement associatif</p>
                  <p className="text-content/90">
                    En tant qu'association à but non lucratif, nous nous engageons à ne jamais utiliser vos données à des fins commerciales ou les partager avec des entreprises tierces.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <SectionTitle>9. Cookies et technologies similaires</SectionTitle>
              <div className="prose max-w-none text-content leading-relaxed">
                <p className="mb-6">
                  Nous utilisons des cookies de manière limitée et respectueuse de votre vie privée.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-green-500/10 to-green-600/20 p-6 rounded-xl">
                    <h4 className="font-bold text-green-600 mb-3">Cookies essentiels</h4>
                    <ul className="list-disc pl-4 text-sm text-content/80 space-y-2">
                      <li>Maintien de votre session</li>
                      <li>Sécurité du site (protection CSRF)</li>
                      <li>Préférences de langue</li>
                    </ul>
                  </div>
                  <div className="bg-gradient-to-br from-red-500/10 to-red-600/20 p-6 rounded-xl">
                    <h4 className="font-bold text-red-600 mb-3">Ce que nous n'utilisons PAS</h4>
                    <ul className="list-disc pl-4 text-sm text-content/80 space-y-2">
                      <li>Cookies publicitaires</li>
                      <li>Tracking de sites tiers</li>
                      <li>Profilage comportemental</li>
                      <li>Vente de données à des tiers</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <SectionTitle accent="secondary">10. Contact et réclamations</SectionTitle>
              <div className="prose max-w-none text-content leading-relaxed">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-bold text-contrast mb-4">Responsable du traitement</h4>
                    <div className="bg-gradient-to-br from-secondary/10 to-secondary/20 p-6 rounded-xl">
                      <h5 className="font-bold text-secondary mb-2">CHALKY PRODUCTION</h5>
                      <p className="text-content/90 text-sm mb-2">Association loi 1901</p>
                      <p className="text-content/90 text-sm mb-2">26 Bis rue Aristide Briand, 44230 Saint-Sébastien-sur-Loire</p>
                      <p className="text-content/90 text-sm">
                        Email :{" "}
                        <a href={`mailto:${contact_email}`} className="text-secondary font-semibold hover:underline">
                          {contact_email}
                        </a>
                      </p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-contrast mb-4">Autorité de contrôle</h4>
                    <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/20 p-6 rounded-xl">
                      <h5 className="font-bold text-blue-600 mb-2">CNIL</h5>
                      <p className="text-content/90 text-sm mb-2">
                        Si vous estimez que vos droits ne sont pas respectés, vous pouvez adresser une réclamation à la CNIL
                      </p>
                      <p className="text-content/90 text-sm">
                        <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:underline">
                          www.cnil.fr
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-16">
            <div className="bg-contrast p-8 rounded-3xl text-white">
              <h3 className="text-2xl font-bold mb-4">Questions sur vos données personnelles ?</h3>
              <p className="text-white/90 mb-6">
                Notre équipe respecte votre vie privée et répond à toutes vos questions
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <a
                  href={`mailto:${contact_email}`}
                  className="inline-block px-8 py-4 bg-white text-contrast font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all duration-300"
                >
                  Nous contacter
                </a>
                <Link
                  href={paths.mentions_legales}
                  className="inline-block px-8 py-4 bg-transparent text-white font-bold rounded-full border-2 border-white hover:bg-white hover:text-contrast transition-all duration-300"
                >
                  Mentions légales
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer
        brand={brand}
        nav={nav}
        footer={footer}
        contactEmail={contact_email}
        paths={{
          mentionsLegales: paths.mentions_legales,
          confidentiality: paths.confidentiality,
        }}
      />
    </>
  )
}
